import { CONNECT_OUTCOMES, CONNECT_RETURN_PARAMS, type ConnectOutcome } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The return from Atlassian, read once and taken out of the address bar.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE QUERY IS REMOVED AFTER IT IS READ.** A `?connected=ok` left in the address bar
 * re-fires on every reload and gets bookmarked and shared — and `?connected=state` shared with a
 * colleague tells them their connection failed when they never made one.
 *
 * ⚠️⚠️ **ONLY THE CONNECT PARAMS ARE STRIPPED, NOT THE WHOLE QUERY.** The first version cleared
 * everything, which took `?section=integrations` with it — so the callback landed somebody on the
 * right pane and then immediately threw them back to Profile.
 *
 * ⚠️ **One reader for both screens the callback returns to** (the account screen and the instance
 * wizard), so a parameter the server adds is stripped by both rather than lingering in one.
 *
 * ⚠️ An unknown code is ignored rather than guessed at: it can only come from a version of the
 * server this build does not know, and inventing a message for it would be inventing a fact.
 */
export interface ConnectReturnRead {
  readonly outcome: ConnectOutcome;
  /** ⚠️ The provider's own sentence, untranslated on purpose — it is what somebody searches for. */
  readonly detail: string | null;
  /** The instance the flow was for — absent when the server could not vouch for it. */
  readonly instanceId: string | null;
  /** `wrong_site`: the site hosts the consent covered. */
  readonly picked: readonly string[];
  /** `wrong_site`: the host this instance uses. */
  readonly expected: string | null;
}

export function takeConnectReturn(): ConnectReturnRead | null {
  const params = new URLSearchParams(window.location.search);
  const outcome = params.get(CONNECT_RETURN_PARAMS.outcome);
  if (outcome === null) return null;

  const picked = params.get(CONNECT_RETURN_PARAMS.picked);
  const read = {
    detail: params.get(CONNECT_RETURN_PARAMS.detail),
    instanceId: params.get(CONNECT_RETURN_PARAMS.instance),
    picked:
      picked === null
        ? []
        : picked
            .split(',')
            .map((one) => one.trim())
            .filter((one) => one !== ''),
    expected: params.get(CONNECT_RETURN_PARAMS.expected),
  };

  for (const name of Object.values(CONNECT_RETURN_PARAMS)) params.delete(name);
  const rest = params.toString();
  window.history.replaceState(
    null,
    '',
    rest === '' ? window.location.pathname : `${window.location.pathname}?${rest}`,
  );

  if (!(CONNECT_OUTCOMES as readonly string[]).includes(outcome)) return null;
  return { outcome: outcome as ConnectOutcome, ...read };
}
